import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { PurchaseOrderItem } from 'lib/types/models';
import React from 'react';

type Props = {
  currency: string;
  netTotal?: number;
  ivaPercentage?: number;
  taxes?: number;
  total?: number;
  items: PurchaseOrderItem[];
  formatter: (value: number) => string;
};

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const CostBreakdown: React.FC<Props> = ({
  currency,
  netTotal,
  ivaPercentage,
  taxes,
  total,
  items,
  formatter,
}) => {
  const sum = (values: (number | null | undefined)[]): number =>
    values.reduce<number>((acc, v) => acc + (typeof v === 'number' ? v : 0), 0);

  const computedNet =
    typeof netTotal === 'number'
      ? netTotal
      : items.reduce(
          (acc, it) => acc + (it.netPrice ?? 0) * (it.quantity ?? 1),
          0,
        );
  const effectiveIva = typeof ivaPercentage === 'number' ? ivaPercentage : 19;

  const computedTaxes =
    typeof taxes === 'number'
      ? taxes
      : round2((computedNet * effectiveIva) / 100);
  const computedTotal =
    typeof total === 'number' ? total : round2(computedNet + computedTaxes);

  const sumDiscounts = sum(items.map((it) => it.totalDiscounts)) || 0;

  const netFromTotal = round2(computedTotal - computedTaxes);
  const netAfterDeductions = round2(netFromTotal - sumDiscounts);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 1, border: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}
    >
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.65rem' }}
            >
              Total
            </Typography>
            <Typography
              variant="h6"
              sx={{ fontSize: '0.9rem', fontWeight: 700 }}
            >
              {formatter(computedTotal)} {currency}
            </Typography>
          </Box>
          <Box sx={{ mt: 0.75 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2">- Impuestos</Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatter(computedTaxes)} {currency}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2">= Neto</Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatter(netFromTotal)} {currency}
              </Typography>
            </Box>
            <Box sx={{ mt: 0.5 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2">- Descuentos</Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatter(sumDiscounts)} {currency}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2">= Total neto</Typography>
              <Typography variant="body2" fontWeight={700}>
                {formatter(netAfterDeductions)} {currency}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default CostBreakdown;
